import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMyOrganization, listOrganizationUsers, listMyRestaurants } from '../services/api';
import { useUserRole } from '../hooks/useUserRole';
import ActionMenu from '../components/ActionMenu';
import LazyImage from '../components/LazyImage';
import { THUMBNAIL_SIZES } from '../utils/thumbnailUtils';
import { getDateLocale } from '../utils/dateLocale';
import './UsersPage.css';

function UsersPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isOwner } = useUserRole();
  const [organization, setOrganization] = useState(null);
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadOrganizationAndUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOrganizationAndUsers = async () => {
    try {
      setLoading(true);
      setError('');

      // Obtener organizaciones (backend devuelve array)
      const orgResponse = await getMyOrganization();
      console.log('Organizations response:', orgResponse);

      // El backend devuelve { organizations: [...] }, tomar la primera
      const firstOrg = orgResponse.organizations?.[0];

      if (!firstOrg) {
        setError(t('users.noOrganizationFound'));
        return;
      }

      setOrganization(firstOrg);

      // Obtener usuarios y restaurantes
      if (firstOrg?.organizationId) {
        // Cargar usuarios
        const usersResponse = await listOrganizationUsers(firstOrg.organizationId);
        console.log('Users response:', usersResponse);

        // Filtrar usuarios que tengan email válido
        const validUsers = (usersResponse.users || []).filter((user) => user && user.email);
        console.log('Valid users with email:', validUsers);

        setUsers(validUsers);

        // Cargar restaurantes
        try {
          const restaurantsResponse = await listMyRestaurants(firstOrg.organizationId);
          console.log('Restaurants response:', restaurantsResponse);
          setRestaurants(restaurantsResponse.restaurants || []);
        } catch (restError) {
          console.error('Error loading restaurants:', restError);
          setRestaurants([]);
        }
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.message || t('users.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = () => {
    navigate('/users/new');
  };

  const handleEditUser = (userId) => {
    navigate(`/users/edit/${userId}`);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'OWNER':
        return 'role-badge role-owner';
      case 'ADMIN':
        return 'role-badge role-admin';
      case 'COMANDA':
        return 'role-badge role-comanda';
      case 'BARRA':
        return 'role-badge role-barra';
      default:
        return 'role-badge';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'OWNER':
        return t('users.owner');
      case 'ADMIN':
        return t('users.admin');
      case 'COMANDA':
        return t('users.comanda');
      case 'BARRA':
        return t('users.barra');
      default:
        return role;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'status-badge status-active';
      case 'invited':
        return 'status-badge status-invited';
      case 'inactive':
        return 'status-badge status-inactive';
      default:
        return 'status-badge';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return '✓ ';
      case 'invited':
        return '✉ ';
      case 'inactive':
        return '○ ';
      default:
        return '';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return t('common.active');
      case 'invited':
        return t('users.invited');
      case 'inactive':
        return t('common.inactive');
      default:
        return status;
    }
  };

  if (loading) {
    return <div className="loading">{t('users.loadingUsers')}</div>;
  }

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h1>{t('users.title')}</h1>
          {organization && (
            <p className="organization-name">
              {t('users.organization')}: {organization.name}
            </p>
          )}
        </div>
        {isOwner() && (
          <button type="button" className="btn btn-primary" onClick={handleInviteUser}>
            + {t('users.inviteUser')}
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {!isOwner() && <div className="alert alert-warning">{t('users.ownerOnlyWarning')}</div>}

      <div className="users-list">
        <div className="users-grid">
          {users.length === 0 ? (
            <div className="empty-state">
              <p>{t('users.noUsersInOrganization')}</p>
              {isOwner() && (
                <button type="button" className="btn btn-primary" onClick={handleInviteUser}>
                  {t('users.inviteFirstUser')}
                </button>
              )}
            </div>
          ) : (
            users.map((user) => (
              <div key={user.userId || user.SK} className="user-card card">
                <div className="user-header">
                  <div className="user-avatar">
                    {user.avatarUrl ? (
                      <LazyImage
                        src={user.avatarUrl}
                        alt={`${user.firstName || user.email} avatar`}
                        className="avatar-image"
                        thumbnailSize={THUMBNAIL_SIZES.THUMB}
                      />
                    ) : (
                      (user.firstName || user.email || 'U').charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="user-info">
                    <h2>
                      {user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.email || user.userId || 'Usuario sin email'}
                    </h2>
                    {user.email && (user.firstName || user.lastName) && (
                      <p className="user-email">{user.email}</p>
                    )}
                    {user.phone && <p className="user-phone">{user.phone}</p>}
                    <div className="user-badges">
                      <span className={getRoleBadgeClass(user.role)}>
                        {getRoleLabel(user.role)}
                      </span>
                      <span className={getStatusBadgeClass(user.status)} role="status">
                        {getStatusIcon(user.status)}
                        {getStatusLabel(user.status)}
                      </span>
                    </div>
                  </div>
                  {/* Acciones (solo para OWNER) */}
                  {isOwner() && (
                    <div className="user-header-actions">
                      <ActionMenu
                        actions={[
                          {
                            label: t('users.editUser'),
                            icon: 'edit',
                            onClick: () => handleEditUser(user.userId),
                            variant: 'default',
                          },
                        ]}
                      />
                    </div>
                  )}
                </div>

                {user.invitedBy && (
                  <div className="user-meta">
                    <small>
                      {t('users.invitedBy')}:{' '}
                      {user.invitedByName ||
                        users.find((u) => u.userId === user.invitedBy)?.name ||
                        users.find((u) => u.userId === user.invitedBy)?.email ||
                        t('users.deletedUser')}
                    </small>
                  </div>
                )}

                {user.joinedAt && (
                  <div className="user-meta">
                    <small>
                      {t('users.joinedAt')}:{' '}
                      {new Date(user.joinedAt).toLocaleDateString(getDateLocale(i18n.language))}
                    </small>
                  </div>
                )}

                {user.invitedAt && (
                  <div className="user-meta">
                    <small>
                      {t('users.invitedAt')}:{' '}
                      {new Date(user.invitedAt).toLocaleDateString(getDateLocale(i18n.language))}
                    </small>
                  </div>
                )}

                {/* Mostrar restaurantes asignados */}
                {user.allRestaurants ? (
                  <div className="user-meta">
                    <small>🔓 {t('users.accessToAllRestaurants')}</small>
                  </div>
                ) : user.restaurants && user.restaurants.length > 0 ? (
                  <div className="user-meta">
                    <small>
                      {t('users.restaurants')}:{' '}
                      {user.restaurants
                        .map((restId) => {
                          const rest = restaurants.find((r) => r.restaurantId === restId);
                          return rest ? rest.name : restId;
                        })
                        .join(', ')}
                    </small>
                  </div>
                ) : (
                  <div className="user-meta">
                    <small>⚠️ {t('users.noRestaurantsAssigned')}</small>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default UsersPage;
